/**
 * AppShell + Nav (SK-SHELL, T-01). Hosts the tab set (§7: incl. Feedback +
 * Advanced) named in the S2 layout. Tenant/project context is now supplied
 * by the ProjectSelector-seeded ProjectContext (SK-HOME-1, T-20, WI-6a-3)
 * — the earlier fixed placeholder tenant literal is gone. Every tab mounts
 * its real component (SaveScreen/T-11, DailyLog/T-17, DecisionLogView/T-18,
 * MasterActionListView/T-19, ReportsView/T-22, AdvancedModeGate/T-24).
 *
 * The Capture flow here stops at the optimistic HELD ack (§1.3) — the
 * HELD→QUEUED release-timing/undo-window UX is T-14's (`finalize.ts`,
 * triage spine), out of this batch's scope; `state-machine.ts`'s
 * `releaseHeld` primitive exists and is exercised directly by this
 * batch's own tests, but nothing in this file calls it yet.
 *
 * WI-6a-2 (1g/6a shell-integration): the full SaveScreen (SK-SAVE,
 * T-11/T-12/T-13 — destination flags, judgment fields, Action/Decision
 * routing) is now MOUNTED as the `save` phase of the capture flow. The
 * Capture surface hands off to SaveScreen on Save; SaveScreen's live
 * outputs (onDestChange → serialized dest `_meta`, onClientGuardFired →
 * the T-28 client-guard signal, onContextChange → the T-10 context block)
 * are mirrored into App state and carried on the committed Event's payload.
 * The Action/Decision toggle drives the coordinator route (see SaveRoute).
 * SaveScreen owns the AC-1g-17 client-report double-confirm entirely now —
 * the earlier inline `capture-client-report` gate this shell carried has
 * been RETIRED (mount-then-migrate-then-retire, W3): its guarding
 * assertions were migrated onto the SaveScreen surface first (see
 * app.dom.test.tsx / save-flow.dom.test.tsx), then the inline section was
 * removed, so the shell was never gate-less at any commit.
 */

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { CaptureHome } from "./components/CaptureHome";
import { ActiveCapture } from "./components/ActiveCapture";
import { TranscribingScreen } from "./components/TranscribingScreen";
import { CaptureTextEntry } from "./components/CaptureTextEntry";
import { CaptureFileEntry } from "./components/CaptureFileEntry";
import { type ModalityOption } from "./components/ModalityPicker";
import { InstallGuide } from "./components/InstallGuide";
import { FeedbackTab } from "./components/FeedbackTab";
import { SettingsMenu } from "./components/SettingsMenu";
import { BottomNav, type TabId } from "./components/BottomNav";
import { ComingSoonModal } from "./components/ComingSoonModal";
import { InstallBanner } from "./components/InstallBanner";
import { DailyLog, deleteLocalRecord, submitCorrection } from "./components/DailyLog";
import { DecisionLogView } from "./components/DecisionLogView";
import { MasterActionListView, type MasterItem, type StatusChange } from "./components/MasterActionListView";
import { ReportsView } from "./components/ReportsView";
import { SaveScreen } from "./components/SaveScreen";
import type {
  ExplicitRoutingChoice,
  RoutingDetection,
  SaveScreenContext,
} from "./triage/save-screen-types";
import { ProjectSelector, selectedProjectRef } from "./components/ProjectSelector";
import { inferredRoutingSuggestionsEnabled } from "./capabilities/inferred-routing-suggestions";
import { PendingConfirmCard } from "./components/PendingConfirmCard";
import { AdvancedModeGate, isSupervisorRole } from "./role/advanced-gate";
import { pendingConfirmStatus } from "./confirm/pending-confirm";
import { useOutboxRecords } from "./shell/use-outbox-records";
import { useReportsController } from "./shell/use-reports-controller";
import { useShellReads } from "./shell/use-shell-reads";
import {
  ProjectContext,
  FIXTURE_CLIENT_CONFIG,
  FIXTURE_CURRENT_USER,
  useProjectContext,
} from "./shell/project-context";
import { mintClientId, mintCaptureId } from "./outbox/idempotency";
import { getSession, onSessionChange } from "./auth/session-store";
import { sessionOwnerPrincipal, type EstablishedSession } from "./auth/workos-session";
import { makeTranscriber, makeBlobPutter, wakeOutboxNow } from "./outbox/runtime";
import type { BlobPutScope } from "./outbox/retry-unit";
import { saveToProject, undoToDraft } from "./outbox/state-machine";
import { rejectionCopy } from "./outbox/rejection-copy";
import { canUndo, releaseNow } from "./triage/finalize";
import { allocateChangeOrdinal, buildCarrierFields } from "./triage/ordinals";
import { markCarrierPayload } from "./triage/carrier-filter";
import { resolveMime, roleForModality } from "./outbox/media-role";
import { sha256HexOfBlob } from "./perf/sha256";
import { markOpened, markSaved } from "./perf/measure";
import {
  deserializeDest,
  explicitNewCaptureDest,
  serializeDest,
  type SerializedDest,
  type DestUIState,
} from "./triage/serialize-dest";
import type { BlobRecord, NewCaptureInput, OutboxRecord } from "./outbox/types";
import { getBlobsForCapture } from "./outbox/db";
import { isAction, isDecisionRaise, payloadStr } from "./components/entry-meta";
import { makeReadClient, type Whoami } from "./read/read-client";
import { findFoldedCanonical } from "./read/log-fold";
import { latestDecisionMutations } from "./read/reconcile";
import { makeFeedbackClient } from "./feedback/feedback-client";
import { fileableWindows } from "./reports/report-client";
import { installConsoleBuffer } from "./feedback/console-buffer";
import { trackKeyboardInset } from "./shell/keyboard-inset";
import { useUpdateChannel } from "./shell/use-update-channel";
import { captureAttr } from "./shell/capture-affordance";
import { guardSave } from "./shell/save-guard";
import { SAVE_FAILED, SAVE_TIMED_OUT } from "./copy/microcopy";
import {
  canRetryTranscription,
  installConnectivityRetry,
  recoveredTranscript,
} from "./transcribe/recovery";
import { readCacheRemoveItem } from "./read/read-cache";
import { makeUsageClient } from "./analytics/usage-client";
import { startActivityTracking } from "./analytics/activity-tracker";
import { UsageAnalyticsView } from "./components/UsageAnalyticsView";
import { fetchPdfBytesStub, fetchSidecarStub } from "./dev/seed-fixtures";

// Pilot hardening gates. These capabilities remain implemented seams rather
// than being deleted; they stay hidden until their workflow is complete enough
// to be useful instead of presenting a dead end to field users.
const ADVANCED_BASIC_ROLES_ENABLED = false;
const SHARE_WITH_SUB_ENABLED = false;

/** §7.9: the one-time "Add to Home Screen" banner rides Chromium's
 * `beforeinstallprompt` event. Where unsupported (iOS Safari), the event never
 * fires and the gear-menu InstallGuide walkthrough is the install path. */
type BeforeInstallPromptEvent = Event & { prompt: () => Promise<void> };
const INSTALL_BANNER_DISMISSED_KEY = "mark-install-banner-dismissed";

type CaptureViewState =
  | { phase: "home" }
  | { phase: "active"; capture_id: string; parts: BlobRecord[]; stream: MediaStream }
  // §7.M3 modality-flow entry surfaces (long-press alt-ingress). Each mints a
  // capture_id up front (markOpened), then lands on the shared "save" phase.
  // Text is a full peer of voice-record: it carries `parts` so photos can be
  // added on the SAME capture screen (the note = the transcript-equivalent),
  // then lands on Save/tag — exactly the voice ActiveCapture→Save shape.
  | { phase: "text-entry"; capture_id: string; parts: BlobRecord[] }
  | { phase: "upload-entry"; capture_id: string }
  | { phase: "video-entry"; capture_id: string }
  | {
      phase: "transcribing";
      capture_id: string;
      parts: BlobRecord[];
      editing?: OutboxRecord;
      from?: "log" | "report";
    }
  | {
      phase: "save";
      capture_id: string;
      parts: BlobRecord[];
      transcript?: string;
      low_confidence?: boolean;
      review_token?: string;
      routing_detection?: RoutingDetection;
      routing_review_pending?: boolean;
      routing_choice?: ExplicitRoutingChoice;
      /** §4.3 / canon `edit: () => set({tab:"capture", phase:"save", saveFrom:"log"})`
       * — this save screen was re-opened from an ALREADY-SAVED daily-log entry
       * rather than started from a fresh capture. It drives the "‹ Daily Logs"
       * back label, the return destination, the field prefill, and which commit
       * path Save takes (pre-wire re-save vs supersede carrier). Absent for
       * every ordinary capture, so nothing about the capture flow changes. */
      editing?: OutboxRecord;
      /** Which surface the edit was opened from — drives the back label and the
       * return destination (canon's `saveFrom`). */
      from?: "log" | "report";
    }
  | { phase: "saved" };

/** The coordinator routing table (WI-6a-2, DS-1g-04 §4.7 / §8.1): the human
 * Action/Decision toggle picks which skill op the committed Event routes to.
 * This is carried on the wire as `payload.route`, so the emitted Event's
 * route reaches the coordinator (Action → Skill #2 `upsert`; Decision-raise
 * → Skill #3 `raise`; Decision-resolve → Skill #3 `resolve`, carrying the
 * picked decision + rationale). SaveScreen forks on `toggle`/`decisionMode`
 * ONLY (never on `client_intent`, W4/6e).
 *
 * As of #13 (2026-07-27) the `raise` and `resolve` legs are LIVE end to end —
 * the coordinator maps them onto Skill #3 (`carrier.py`: raise fans out
 * alongside the field-log entry, resolve overrides the route since it carries no
 * field content). Before that, both routes reached the coordinator and were
 * silently ignored: the capture landed as a plain field-log entry and the
 * decision register never moved.
 *
 * §7.M6: `assign_owner` LANDS as of #16 (2026-07-28). It was the one route that
 * genuinely needed a signed-skill change — Skill #3's ops were Seed/Raise/Resolve/
 * View with no way to change a decision's judgment after the fact, and adding one
 * put a new value in the signed `op` enum, so it cost a re-minting ceremony. The
 * server op is a GENERAL `amend` over the whole `DecisionCategory` (not a narrow
 * owner setter), which is why the next judgment field a surface learns to set will
 * need no second ceremony. The route keeps its original `assign_owner` name so a
 * carrier sitting in an outbox from before #16 still lands when the phone syncs. */
/** Skill #1's version on every emission this app makes. Was a bare "0.1.0"
 * repeated at five emission sites until #23a added a sixth (a correction from a
 * restored device) — and a correction claiming a different version than the
 * capture it supersedes is exactly the drift a repeated literal invites. */
const CAPTURE_SKILL_VERSION = "0.1.0";
/** How long a Save may take before the human is told it didn't land. A local
 * IndexedDB write normally finishes in well under a second. */
const SAVE_TIMEOUT_MS = 15_000;

type SaveRoute =
  | { skill: "master_action_list"; op: "upsert" }
  | { skill: "decision_log_dayone"; op: "raise" }
  | {
      skill: "decision_log_dayone";
      op: "resolve";
      /** The target decision's CAPTURE id (`Decision.id`) — Skill #3 resolves by
       * capture id, not by the `decision_id` register key (`resolve.rs`:
       * `decisions.find(|d| d.id == ri.decision_ref)`). This carried
       * `decision_id` until #13; every resolve was rejected `UnknownCaptureId`. */
      decision_ref: string;
      rationale: string;
      /** MANDATORY on the wire — `resolve.rs` fail-closes (no write) without it.
       * Allocated ONLY by `decisionResolveRoute` below. */
      change_ordinal: number;
    }
  | {
      skill: "decision_log_dayone";
      op: "assign_owner";
      /** The target decision's CAPTURE id, same as a resolve. */
      decision_ref: string;
      owner: string;
      /** MANDATORY on the wire — `amend.rs` fail-closes (no write) without it, the
       * same INV-4 rule a resolve obeys. Allocated ONLY by `decisionAmendRoute`. */
      change_ordinal: number;
    };

/** The ONE locus that builds a decision-resolve route, so `change_ordinal` can
 * never be forgotten at a call site (three surfaces emit resolves: the save
 * screen's picker, the Decisions-tab picker, and the decision detail).
 *
 * `highestInCollapsedRead` is 0: the server's collapsed read FOLDS the ordinal
 * away (`Decision` has no `change_ordinal` field — the fold's whole job is to
 * report the winner, not the pool), so the read half of §4.6's allocation
 * formula is genuinely unavailable here. The device cursor half is durable and
 * strictly-monotonic-per-canonical on its own, which is the same position
 * `submitCorrection` takes for the same reason. */
async function decisionResolveRoute(decision_ref: string, rationale: string): Promise<SaveRoute> {
  const change_ordinal = await allocateChangeOrdinal(decision_ref, 0);
  return { skill: "decision_log_dayone", op: "resolve", decision_ref, rationale, change_ordinal };
}

/** The owner-assign twin of `decisionResolveRoute` (#16), for the same reason:
 * `change_ordinal` can never be forgotten at a call site.
 *
 * It shares the SAME per-canonical cursor as the resolve route, and that is
 * load-bearing rather than incidental — a resolve and an amend both spend ordinals
 * against one decision, so two separate cursors would hand out the same number
 * twice and one carrier would silently lose the INV-4 comparison. */
async function decisionAmendRoute(decision_ref: string, owner: string): Promise<SaveRoute> {
  const change_ordinal = await allocateChangeOrdinal(decision_ref, 0);
  return { skill: "decision_log_dayone", op: "assign_owner", decision_ref, owner, change_ordinal };
}

export function App({ onSignOut }: { onSignOut?: () => void } = {}) {
  // C-mount (FIX-B2): the shell reads its identity + config from the INSTALLED
  // session (set by AuthGate/callback), never a compile-time fixture. Under the
  // AuthGate the shell only mounts once a session exists, so getSession() is
  // populated in the real app; the fixtures remain ONLY as the null-fallback for
  // tests that render <App/> directly without the gate. A supervisor session now
  // carries config.role="member" (derived), so it can no longer inherit the
  // fixture's "tenant_admin" advanced UI (Codex blocker 3).
  const [session, setSession] = useState<EstablishedSession | null>(() => getSession());
  useEffect(() => onSessionChange(setSession), []);
  const baked = session ? session.config : FIXTURE_CLIENT_CONFIG;
  const currentUser = session ? session.current_user : FIXTURE_CURRENT_USER;
  // Ask the coordinator who this device is. Until people have their own codes
  // this returns the shared identity and nothing changes; once they do, it is
  // what stops a person being OFFERED projects they cannot open — the picker was
  // built from a compile-time fixture, so a scoped person saw everyone's
  // projects and only discovered the truth by tapping one and getting a 403.
  const [whoami, setWhoami] = useState<Whoami | null>(null);
  useEffect(() => {
    const client = makeReadClient();
    if (!client) return;
    let live = true;
    void client.fetchWhoami().then((w) => {
      if (live) setWhoami(w);
    });
    return () => {
      live = false;
    };
  }, []);

  // `scoped: false` is a tenant_admin, whose project check is skipped entirely —
  // their empty project_ids means EVERYTHING in the tenant, so the baked list is
  // the right thing to show them. A failed lookup also falls back, because being
  // unable to pick a project is a worse outcome than showing one that 403s.
  const config = useMemo(
    () =>
      whoami && whoami.scoped && whoami.project_ids.length > 0
        ? { ...baked, tenant_id: whoami.tenant_id || baked.tenant_id, project_ids: whoami.project_ids }
        : baked,
    [baked, whoami],
  );
  const [projectRefState, setProjectRefState] = useState<string | null>(() => {
    try {
      return localStorage.getItem("mark.project_ref");
    } catch {
      return null;
    }
  });
  // §align: persist the human's last project pick so it survives reloads / PWA
  // restarts (sticky last-selected). project_ref is null ONLY on the very
  // first-ever open — then ProjectSelector force-opens the picker to pick.
  function setProjectRef(ref: string) {
    try {
      localStorage.setItem("mark.project_ref", ref);
    } catch {
      /* private mode / storage disabled → in-memory only */
    }
    setProjectRefState(ref);
  }
  // AC-1g-07 / TW-1g-PROJECT-INFER: the exposed project_ref is ONLY ever a
  // member of the provisioned scope — `selectedProjectRef` is the single
  // accessor that defends against a stale pick surviving a scope shrink.
  const project_ref = selectedProjectRef(config.project_ids, projectRefState);
  return (
    <ProjectContext.Provider value={{ current_user: currentUser, config, project_ref, setProjectRef }}>
      <AppShell onSignOut={onSignOut} />
    </ProjectContext.Provider>
  );
}

function AppShell({ onSignOut }: { onSignOut?: () => void }) {
  const { current_user, config, project_ref, setProjectRef } = useProjectContext();
  const owner_principal = sessionOwnerPrincipal({ current_user, config });
  const projectScopeRef = useRef({ project_ref, generation: 0 });
  if (projectScopeRef.current.project_ref !== project_ref) {
    projectScopeRef.current = { project_ref, generation: projectScopeRef.current.generation + 1 };
  }
  const [tab, setTab] = useState<TabId>("capture");
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  // §7.9: the settings gear owns Feedback + Install; `beforeinstallprompt` is
  // stashed for the one-time home-screen banner.
  const [settingsOpen, setSettingsOpen] = useState(false);
  // #8: start capturing console errors as early as the shell mounts. On a phone
  // there is no console to read, so every signal the 2026-07-27 defects emitted
  // (stale bundle, dropped endpoint, wedged outbox) was simply lost. Idempotent
  // and pass-through — nothing changes for anyone debugging with devtools open.
  useEffect(() => installConsoleBuffer(), []);
  // Publish the on-screen keyboard's overlap so app-main can make room for it —
  // the shell is pinned to `100svh` and cannot shrink for a keyboard, so without
  // this the bottom of a form is covered with nothing to scroll to.
  useEffect(() => trackKeyboardInset(), []);
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [captureView, setCaptureView] = useState<CaptureViewState>({ phase: "home" });
  const inferredRoutingEnabled = inferredRoutingSuggestionsEnabled();
  const routingReviewSequence = useRef(0);
  const routingReviewAbort = useRef<AbortController | null>(null);
  const updateSafe = captureView.phase === "home" || captureView.phase === "saved";
  const { updateReady, updateStatus, bundleName, handleUpdateCheck, handleUpdateApply } = useUpdateChannel(
    updateSafe,
    () => setSettingsOpen(true),
  );

  // #26 (durability): persist the capture to the outbox as HELD when it enters
  // the "transcribing" leg — BEFORE the (potentially long/stalling) network
  // round-trip — so a stall or a force-quit during transcribe can't lose it.
  // Today a capture is only crash-durable at Save; during "transcribing" it
  // lives purely in volatile memory (this is the gap that stranded the capture
  // behind the "Transcribing…" hang). Untyped/minimal (parts + project_ref); the
  // real Save (commitRoutedCapture) overwrites/enriches by the SAME capture_id
  // (idempotent upsert) — and because the human only reaches Save AFTER the
  // transcribe leg resolves (seconds later), the pre-persist has long completed,
  // so there is no clobber race. Scoped to "transcribing" for exactly that
  // reason: the "save" phase would race the imminent commit. A HELD record NEVER
  // sends until the human Saves (releaseHeld fires only from triage/finalize),
  // so this is pure durability, not a premature emission. Uses only the public
  // saveToProject — the LOCKED outbox/triage/confirm/perf spine is untouched.
  // (Caveat: a Back/discard leaves an orphan HELD row; cleanup would need a
  // delete in the locked spine — tracked follow-up. Photo-only/no-STT captures
  // skip straight to Save with no network wait, so they are not pre-persisted.)
  const prepersistedRef = useRef<string | null>(null);
  useEffect(() => {
    const cv = captureView;
    if (cv.phase !== "transcribing") return;
    if (cv.editing) return; // Restored rows are already durable under this capture_id.
    if (project_ref === null) return;
    if (prepersistedRef.current === cv.capture_id) return;
    prepersistedRef.current = cv.capture_id;
    void saveToProject({
      capture_id: cv.capture_id,
      client_id: mintClientId(),
      owner_principal,
      tenant_id: config.tenant_id,
      skill: "daily_field_log_capture",
      skill_version: CAPTURE_SKILL_VERSION,
      source_refs: [],
      parts: cv.parts,
      payload: { project_ref },
    }).catch(() => {
      // Best-effort durability write; a failure must never block/break the
      // capture flow (the real Save persists again by the same capture_id).
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureView]);

  function retryCurrentTranscription(): void {
    setCaptureView((prev) => {
      if (
        prev.phase !== "save" ||
        !canRetryTranscription(prev.transcript, prev.parts, stt !== null)
      ) return prev;
      return {
        phase: "transcribing",
        capture_id: prev.capture_id,
        parts: prev.parts,
        editing: prev.editing,
        from: prev.from,
      };
    });
  }

  // If a voice capture fell through to a blank Save screen while offline, reuse
  // the exact same durable capture/media identities as soon as connectivity
  // returns. Android installed PWAs do not reliably emit `online`, so the
  // shared installer also wakes on focus, pageshow, and visible foregrounding.
  // The visible retry remains the deterministic fallback.
  useEffect(() => {
    return installConnectivityRetry(retryCurrentTranscription);
  });
  // §7.M4: the shared per-item review/edit surface — an app-level overlay keyed
  // by capture_id, reached from Daily Logs today and the Reports deep-link (M7).
  // `from` drives the "‹" back label/return (the preview's `saveFrom`).

  // §7.M7 dead-control sweep (§7.10 — no silent no-ops): a control whose live
  // leg isn't wired yet surfaces this coming-soon modal instead of a dead tap.
  const [comingSoon, setComingSoon] = useState<string | null>(null);

  // B3: live transcribe/upload handles, built lazily from the baked config the
  // first time they're needed. Guarded so a test/jsdom mount with no baked
  // VITE_MARK_ENDPOINT doesn't throw at shell mount — the transcribing phase is
  // only reached in the live flow (and tests inject their own via props path).
  const [stt] = useState(() => {
    try {
      // putBlob is a CLASS METHOD — it must stay bound to its HttpBlobPutter
      // instance. Extracting it bare (`makeBlobPutter().putBlob`) drops `this`,
      // so when TranscribingScreen calls it as a plain function `this.cfg` is
      // undefined and the PUT throws BEFORE any fetch is issued — swallowed by
      // that leg's empty catch, so the audio never uploads and the transcript
      // comes back empty. transcriber survives because it's called as
      // `transcriber.transcribe(...)` (method call keeps `this`).
      const blobPutter = makeBlobPutter();
      return { transcriber: makeTranscriber(), putBlob: blobPutter.putBlob.bind(blobPutter) };
    } catch {
      return null;
    }
  });

  // WI-6a-1: the read-only Log tab reads the device-local capture store via
  // the shell hook (refresh-on-change over the dispatcher wake sources).
  const { records: outboxRecords, refresh: refreshOutbox } = useOutboxRecords(
    owner_principal,
    import.meta.env.MODE === "test",
  );
  // Per-decision sync state of this device's own assign/resolve changes, so the
  // Decisions tab never shows a queued or refused change as done.
  const decisionMutations = useMemo(() => latestDecisionMutations(outboxRecords), [outboxRecords]);

  // T-1g-READ: the LIVE server read for Decisions + Action List. Built once
  // from the baked config; `null` when no endpoint was baked in (dev/test),
  // in which case the tabs fall back to cache and then to the dev fixtures.
  const [readClient] = useState(() => makeReadClient());
  // Successful discards are hidden locally before the follow-up read returns.
  // This also fences an older in-flight read response during the transition.
  const [discardedLogIds, setDiscardedLogIds] = useState<Set<string>>(() => new Set());
  // #7/#8: feedback + debug logs take their own server path, never the capture
  // outbox (see FeedbackTab's module doc for why the old path never landed).
  const [feedbackClient] = useState(() => makeFeedbackClient());
  const usageClient = useMemo(
    () => makeUsageClient({
      owner_principal,
      tenant_id: config.tenant_id,
      actor_id: current_user.user_id,
    }),
    [owner_principal, config.tenant_id, current_user.user_id],
  );
  useEffect(() => {
    if (!usageClient) return;
    const stopTracking = startActivityTracking(
      usageClient,
      () => projectScopeRef.current.project_ref,
    );
    return () => {
      stopTracking();
      usageClient.stop();
    };
  }, [usageClient]);

  function selectProjectForUse(project: string) {
    setProjectRef(project);
    void usageClient?.track("project_selected", project);
  }

  function trackTabView(nextTab: TabId) {
    const event = nextTab === "reports"
      ? "report_viewed"
      : nextTab === "master"
        ? "action_viewed"
        : nextTab === "decisions"
          ? "decision_viewed"
          : null;
    if (event) void usageClient?.track(event, project_ref);
  }

  function trackRouteMutation(route: SaveRoute | null | undefined) {
    if (route?.skill === "master_action_list") {
      void usageClient?.track("action_mutated", project_ref);
    } else if (route?.skill === "decision_log_dayone") {
      void usageClient?.track("decision_mutated", project_ref);
    }
  }

  const {
    logsRead,
    projectRefused,
    unseenRejections,
    dismissRejection,
    projectLocalRecords,
    serverOnlyLogRows,
    authoritativeServerLogRows,
    serverOrdinalOf,
    liveDecisions,
    liveActions,
  } = useShellReads({
    project_ref,
    readClient,
    tenant_id: config.tenant_id,
    skill_version: CAPTURE_SKILL_VERSION,
    outboxRecords,
    refreshOutbox,
    discardedLogIds,
  });
  const {
    filedState: currentFiledState,
    fetchFiledSidecar,
    fetchReportPhoto,
    downloadFiled,
    fetchWindowPreview,
    setReportItemIncluded,
    fileWindow,
    refileFiled,
    fileReport,
  } = useReportsController({
    project_ref,
    projectScopeRef,
    tenant_id: config.tenant_id,
    user_id: current_user.user_id,
    usageClient,
    setComingSoon,
    outboxRecords,
    refreshOutbox,
    authoritativeServerLogRows,
    serverOrdinalOf,
    releaseForDelivery,
    skill_version: CAPTURE_SKILL_VERSION,
  });

  // WI-6a-2: SaveScreen's live wire outputs, mirrored into App state so the
  // committed Event carries them. `destMeta` is the already-serialized dest
  // shape from SaveScreen's onDestChange (serializeDest is the ONE locus —
  // App never re-derives it). `clientGuardFired` counts the T-28 signal.
  // `contextAnswers` rides payload.anchor_text/room (T-10, §8.1).
  const [destMeta, setDestMeta] = useState<SerializedDest>({});
  const [clientGuardFired, setClientGuardFired] = useState(false);
  const [contextAnswers, setContextAnswers] = useState<SaveScreenContext | null>(null);
  // A Save tap must always end in the next screen or a visible error, never in
  // nothing: on iPhone Safari a photo made the local write hang with no error,
  // and the fire-and-forget commit left Save silently dead (2026-10-01).
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const savingRef = useRef(false);
  useEffect(() => {
    if (captureView.phase !== "save") setSaveError(null);
  }, [captureView.phase]);

  function runSave(commit: () => Promise<unknown>): void {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    void guardSave(Promise.resolve().then(commit), SAVE_TIMEOUT_MS).then((outcome) => {
      savingRef.current = false;
      setSaving(false);
      if (outcome.ok) return;
      if (outcome.reason === "timed_out") {
        console.error(`[save] capture save timed out after ${SAVE_TIMEOUT_MS}ms`);
        setSaveError(SAVE_TIMED_OUT);
      } else {
        console.error("[save] capture save failed", outcome.error);
        setSaveError(SAVE_FAILED);
      }
    });
  }
  // Judgment-emit: the human-set criticality tier + owner, mirrored from
  // SaveScreen's onJudgmentChange and carried on every commit's payload bag
  // (the backend ActionItemInput already expects criticality_tier/owner).
  const [judgment, setJudgment] = useState<{
    criticality_tier: string | null;
    owner: string | null;
    // M-TAG1 (BUI-92): the first-tier tags, mirrored from SaveScreen's
    // onJudgmentChange and carried on the commit payload bag exactly like
    // criticality_tier/owner (the coordinator forwards phase/trade the same way).
    phase: string | null;
    trade: string | null;
    decides: string | null;
    room: string | null;
    decides_touched: boolean;
    room_touched: boolean;
  }>({
    criticality_tier: null,
    owner: null,
    phase: null,
    trade: null,
    decides: null,
    room: null,
    decides_touched: false,
    room_touched: false,
  });

  /** Release a just-saved HELD capture to QUEUED so the live dispatcher
   * (runtime.ts) actually POSTs /mark/message. WITHOUT this, saveToProject
   * leaves every emission HELD forever and nothing reaches the coordinator —
   * the "Saved to project" ack would be local-only (the exact "saved but
   * nothing landed" bug). Immediate release, NOT the 60s scheduleRelease timer:
   * there is no undo-toast UI wired yet, and a mobile-throttled timer that
   * never fires would re-strand the record (recoverOnStartup only rescues
   * SENDING, not HELD). A release failure must never break the UI — the record
   * stays HELD and a later Save can recover it. Calls only the public
   * triage/finalize primitive; the LOCKED outbox spine is untouched. */
  async function releaseForDelivery(capture_id: string): Promise<boolean> {
    try {
      await releaseNow(capture_id);
      // Send it NOW. Releasing only marks the record QUEUED; the dispatcher's
      // wake sources did not include "something was just released", so it then
      // waited up to the 30s timer floor before leaving the device — which is
      // the whole of the "almost 30s" a report change took to show. Not awaited:
      // the record is already durable, and blocking this path on a round trip
      // would trade a delay nobody sees for one they do.
      wakeOutboxNow();
      return true;
    } catch (err) {
      console.error("[mark] releaseForDelivery failed:", capture_id, err);
      return false;
    }
  }

  /** Commit a routed capture group to the outbox (WI-6a-2). Carries the
   * coordinator route + the dest `_meta` (flat in payload, matching this
   * file's established shape) + the T-10 context answers. The Save button
   * on SaveScreen is the only caller; it forks on toggle/decisionMode ONLY,
   * NEVER on client_intent (W4/6e — client_intent is a save-time _meta flag
   * consumed server-side, never a client-side routing input). */
  async function commitRoutedCapture(
    capture_id: string,
    parts: BlobRecord[],
    route: SaveRoute | null,
    transcript?: string,
  ) {
    // AC-1g-07 (TW-1g-PROJECT-INFER): project_ref is UNCONDITIONAL on every
    // emission. SaveScreen's `canRoute` gate already blocks Save until the
    // human picks a project, so reaching here with no selection is a wiring
    // bug — refuse to build the input rather than silently omit project_ref.
    if (project_ref === null) return;
    const pair = markSaved(capture_id);
    const payload: Record<string, unknown> = {
      // The dest `_meta` (report_dest / client_intent) serialized by
      // SaveScreen — spread flat, byte-identical to this file's prior shape.
      ...explicitNewCaptureDest(destMeta),
    };
    // §7.11: a typed capture carries the coordinator route; an untyped one
    // (route === null) commits with NO route — a plain daily_field_log_capture.
    if (route) payload.route = route;
    // Judgment-emit: the human-set criticality tier + owner ride the payload
    // bag (the coordinator forwards them into ActionItemInput.criticality_tier/
    // owner — that server leg is tracked separately). Absent when the human
    // left them unset; NEVER AI-seeded (AC-1g-06).
    if (judgment.criticality_tier) payload.criticality_tier = judgment.criticality_tier;
    if (judgment.owner) payload.owner = judgment.owner;
    // M-TAG1 (BUI-92): the first-tier tags ride the same payload bag, conditional
    // on the human having set them (dirty-tracked at SaveScreen — absent means
    // unchanged; NEVER AI-seeded, AC-1g-06). The coordinator forwards phase/trade
    // into the master-action-list carrier the same way it does criticality/owner
    // (that server leg is tracked separately, not this PWA ticket).
    if (judgment.phase) payload.phase = judgment.phase;
    if (judgment.trade) payload.trade = judgment.trade;
    if (judgment.decides) payload.decides = judgment.decides;
    if (judgment.room) payload.room = judgment.room;
    // §7.M3: a capture's text (a typed Text-modality note, or later an STT
    // transcript) rides the payload. Absent for photo/audio/video-only captures
    // that have no text yet ("transcript pending").
    if (transcript) payload.transcript = transcript;
    if (captureView.phase === "save" && captureView.review_token) {
      payload.review_confirmation = { token: captureView.review_token };
    }
    // T-28 client-guard-fired SIGNAL (B-EMIT-GAP): SaveScreen fires it only
    // when the client-report second confirm is affirmed. This build carries
    // the signal on the payload; T-28 owns the countable wire emission.
    if (clientGuardFired) {
      payload.client_guard_fired = true;
    }
    if (contextAnswers && (contextAnswers.room || contextAnswers.intent)) {
      payload.anchor_text = contextAnswers.intent;
      payload.room = contextAnswers.room;
    }
    // WI-6a-3: project_ref rides the open payload bag (the §4.7 locus for
    // non-schema-literal fields — same convention as `route`/dest `_meta`);
    // `project_ref` on EVERY emission comes from ProjectSelector (AC-1g-07,
    // design §8.1). NewCaptureInput carries no project_ref field of its own,
    // so this is the emission's project_ref locus. UNCONDITIONAL: the
    // project_ref === null early-return above makes this an invariant.
    payload.project_ref = project_ref;
    const input: NewCaptureInput = {
      capture_id,
      client_id: mintClientId(),
      owner_principal,
      // Real tenant context is the ProjectSelector-supplied provisioned
      // config (SK-HOME-1, T-20) — the fixed dev placeholder is GONE
      // (WI-6a-3, TW-1g-PROJECT-INFER).
      tenant_id: config.tenant_id,
      skill: "daily_field_log_capture",
      skill_version: CAPTURE_SKILL_VERSION,
      source_refs: [],
      parts,
      payload,
      timing: pair,
    };
    await saveToProject(input);
    // Release HELD -> QUEUED so the dispatcher delivers this capture (see
    // releaseForDelivery). This is what actually sends /mark/message.
    await releaseForDelivery(capture_id);
    void usageClient?.track("capture_saved", project_ref);
    trackRouteMutation(route);
    setDestMeta({});
    setClientGuardFired(false);
    setContextAnswers(null);
    setJudgment({ criticality_tier: null, owner: null, phase: null, trade: null, decides: null, room: null, decides_touched: false, room_touched: false });
    setCaptureView({ phase: "saved" });
  }

  const hasPhotoPart = (parts: BlobRecord[]) => parts.some((p) => p.role === "primary_photo");

  /** #39-save: the raw `primary_photo` blobs of an in-flight capture, for the
   * SaveScreen thumbnail strip (pre-commit, so IndexedDB has nothing yet). */
  const photoParts = (parts: BlobRecord[]) =>
    parts.filter((p) => p.role === "primary_photo").map((p) => p.blob);

  /** §7.M6: the Decision Detail (DecisionLogView) emit path. A resolve or an
   * owner-assign from the detail screen mints a fresh capture group carrying
   * the Skill #3 route onto the SAME §1 outbox.
   * Replaces the two dead `() => {}` props on the DecisionLogView mount, per
   * the S8 read-gap decision (keep the edit/emit IN the PWA; the read reflects
   * it once 1l-T-READ lands). AC-1g-07: project_ref is UNCONDITIONAL — refuse
   * to emit without a selected project. */
  async function emitDecisionRoute(
    route: SaveRoute,
    reports?: { daily: boolean; weekly: boolean; client?: boolean },
  ) {
    // Refuse loudly: a caller awaiting this must not read "no project" as queued.
    if (project_ref === null) throw new Error("No project selected — the change was not saved.");
    const payload: Record<string, unknown> = { route, project_ref };
    // §7.M7: optional report-routing rides the resolve emission's dest _meta —
    // built through the ONE serializeDest locus (App never re-derives it).
    // The client flag is carried through from DecisionLogView, where it can ONLY
    // have been set by the AC-1g-17 two-step confirm (the chip's first tap sets
    // nothing). Previously hardcoded false because the surface had no client
    // chip at all; now that it has one, hardcoding would silently drop a flag
    // the human explicitly affirmed.
    if (reports && (reports.daily || reports.weekly || reports.client)) {
      const dest: DestUIState = {
        choice: { kind: "reports", daily: reports.daily, weekly: reports.weekly },
        client: reports.client === true,
      };
      const meta = serializeDest(dest);
      if (Object.keys(meta).length > 0) payload._meta = meta;
    }
    const capture_id = mintCaptureId();
    await saveToProject({
      capture_id,
      client_id: mintClientId(),
      owner_principal,
      tenant_id: config.tenant_id,
      skill: "daily_field_log_capture",
      skill_version: CAPTURE_SKILL_VERSION,
      source_refs: [],
      parts: [],
      payload,
    });
    await releaseForDelivery(capture_id);
    void usageClient?.track("decision_mutated", project_ref);
    await refreshOutbox();
  }

  // WI-6a-4: the delivered records that carry a stamped `confirm_pending`
  // field (applyAwaitConfirm, confirm/pending-confirm.ts) — the ONLY records
  // PendingConfirmCard renders for. The card keys on
  // `payload[CONFIRM_PENDING_KEY]`; here we pre-filter to that stamped set so
  // the Log tab surfaces one card per pending ASK.
  const pendingConfirmRecords = outboxRecords.filter(
    (r) => pendingConfirmStatus(r) !== "none",
  );


  /** CANON (Mark-PWA-Spec: `edit: () => set({tab:"capture", phase:"save",
   * saveFrom:"log"})`, design §4.3 "re-opens the save screen, every field/flag
   * as it was"): tapping a daily-log row opens the EDITABLE save screen, not a
   * read-only card. Photo parts are hydrated from the durable blob store —
   * unlike a fresh capture, a saved entry's blobs are already in IndexedDB, so
   * the same `parts` shape drives hasPhotoPart/photoBlobs with no special case.
   *
   * The old bespoke LogEntryReview overlay is gone. It also took the whole tab
   * tree with it while open, which is what made the bottom nav a dead tap (#3);
   * the save screen lives INSIDE the capture tab, so nav just works and an
   * in-progress edit survives a tab switch. */
  /** Returns false when the entry could not be resolved at all, so a caller that
   * has something to say about a dead tap can say it — silence there was what made
   * report items look broken. */
  async function openLogEntryForEdit(capture_id: string, from: "log" | "report"): Promise<boolean> {
    // #23a: fall back to the SERVER-only rows. A restored device (or a second
    // phone, or a principal reading a project they did not capture) holds no
    // outbox record for an entry the server plainly has, and this lookup
    // returning undefined was the whole reason those rows had no tap target —
    // they could be read and not corrected. `state: "delivered"` routes the save
    // to the supersede branch, which is the only correct path for them anyway.
    // FOLDED, not raw. The daily-log LIST has folded corrections onto the
    // canonical for display since 2026-07-27, but this opened the raw record —
    // so the edit screen showed the entry as first captured while the row above
    // it showed the corrected version. It went unnoticed while every correctable
    // field was text the human retypes anyway, and broke the moment one was a
    // SETTING: an entry already on the daily report re-opened with the box
    // unticked, so ticking it to "take it off" put it on again (Red, 2026-07-31).
    //
    // Resolve from the same server-high-water-aware authority used by report
    // curation. Local canonicals are retained after delivery and may predate a
    // correction already folded by the server.
    const record = findFoldedCanonical(authoritativeServerLogRows, outboxRecords, capture_id);
    if (!record) return false;
    const parts = await getBlobsForCapture(capture_id);
    setTab("capture");
    setCaptureView({
      phase: "save",
      capture_id,
      parts,
      transcript: recoveredTranscript(record, parts),
      editing: record,
      from,
    });
    return true;
  }

  /** Save on a RE-OPENED entry. Which path it takes depends ONLY on which side
   * of the §4.2 finalize line the entry is on — the same rule the old surface
   * encoded, now behind one Save button instead of a separate grey affordance:
   *
   *   pre-wire (HELD/QUEUED/SENDING) -> direct local edit. Nothing was ever
   *     committed server-side, so the record is undone to DRAFT and rewritten in
   *     place, keeping its capture_id AND client_id: a new client_id could
   *     double-post the same capture if an attempt had already landed.
   *   DELIVERED -> the supersede path (what canon calls "Correct / Re-route").
   *     A NEW carrier referencing the canonical, never a row rewrite (§4.3:
   *     "never a row deletion"), carrying the retyped content so the correction
   *     changes what the entry MEANS downstream, not just an audit line.
   *
   * No rationale is demanded either way: design §4.3 puts "zero added
   * confirmation dialogs" on this path and rule #5 makes nothing mandatory at
   * Save but integrity gates. Fixing a typo should cost one tap. */
  async function commitEditFromLog(
    record: OutboxRecord,
    transcript?: string,
    /* FINDING B (Red, reproduced on device 2026-07-29): "I grabbed a previous log
       and edited it to make it into an action and that one did not make it to the
       action list."

       The route used to be DROPPED here, and not subtly. All four of the save
       screen's route callbacks — Action, Decision-raise, Decision-resolve,
       untyped — collapsed to the identical `commitEditFromLog(record, transcript)`
       call on the edit path, so which button the human tapped was thrown away
       before this function was even entered. The fresh-capture branch right
       alongside them passed a distinct `SaveRoute` into `commitRoutedCapture`,
       which does `if (route) payload.route = route`. Retyping an entry as an
       Action therefore emitted a text-only correction and nothing else, while the
       toggle showed `aria-pressed` and the save screen reported success.

       Nothing was broken server-side: the coordinator's fan-out is keyed on
       `payload.route == {skill: master_action_list, op: upsert}` and handles a
       route-bearing CORRECTION carrier correctly already (#8). It simply never
       received a route from this path.

       ABSENT MEANS UNCHANGED, and that distinction is the whole care in this
       parameter. `null` (the untyped save) must NOT write the key: on the
       supersede path the carrier carries only what it names, and on the pre-wire
       path `edits` is spread over the existing payload — so writing `route: null`
       would let someone fixing a typo silently un-Action an entry they never
       touched the toggle on. Same per-field judgment resolution #16/#21/#22
       already established along INV-4: an owner-only amend must not blank a
       criticality someone else set. */
    route?: SaveRoute | null,
  ) {
    /* DIRTY-TRACKING (Red, 2026-07-30 — two supers on the same project).
       Every value below is SEEDED from the record when the edit screen opens, so
       sending them all back meant a save re-asserted fields the human never
       touched, as this device last saw them. A read can be 30s stale (the
       dispatcher wakes on a timer), so a colleague's amend in that window was
       silently reverted by someone fixing an unrelated typo.

       `record` IS the baseline — it is the local canonical this edit was opened
       from — so a comparison needs no extra state threading. Absent means
       UNCHANGED all the way down the INV-4 chain, which makes omission exactly
       "leave whatever is there alone". */
    const edits: Record<string, unknown> = {};
    const unchanged = (key: string, next: string) => next === (payloadStr(record, key) ?? "");

    if (transcript && !unchanged("transcript", transcript)) edits.transcript = transcript;
    const room = contextAnswers?.room.trim();
    if (room && !unchanged("room", room)) edits.room = room;
    const intent = contextAnswers?.intent.trim();
    if (intent && !unchanged("anchor_text", intent)) edits.anchor_text = intent;
    if (judgment.criticality_tier && !unchanged("criticality_tier", judgment.criticality_tier)) {
      edits.criticality_tier = judgment.criticality_tier;
    }
    if (judgment.owner && !unchanged("owner", judgment.owner)) edits.owner = judgment.owner;
    if (judgment.decides_touched && !unchanged("decides", judgment.decides ?? "")) edits.decides = judgment.decides ?? "";
    if (judgment.room_touched && !unchanged("room", judgment.room ?? "")) edits.room = judgment.room ?? "";
    if (judgment.phase && !unchanged("phase", judgment.phase)) edits.phase = judgment.phase;
    if (judgment.trade && !unchanged("trade", judgment.trade)) edits.trade = judgment.trade;
    /* REPORT DESTINATION (Red, device 2026-07-31): "I cannot open an existing
       daily log and then save it to the report daily or weekly; it never saves
       it."

       Exactly FINDING B's shape, one field over. The fresh-capture branch spreads
       `...destMeta` into its payload; this path never read `destMeta` at all, so
       ticking Daily on a saved entry emitted a correction carrying everything
       EXCEPT the one thing the human came to change — and the screen reported
       success, because the save itself did happen.

       Dirty-tracking needs no comparison here: `destMeta` is `{}` until
       SaveScreen emits, and it emits only on a human tap. Untouched therefore
       writes no key, which is "leave it alone" all the way down INV-4. When the
       human DID touch it, `serializeDest(…, "edit")` has already resolved
       cleared-to-none into an explicit `report_dest: []`, so this spread carries
       a removal as faithfully as an addition. */
    Object.assign(edits, destMeta);
    // `route` is NOT compared: re-tapping Action on an entry that is already an
    // Action is a deliberate re-assertion, and the existing carrier path is
    // idempotent for it. Comparing would also mean reading the route back out of
    // a nested object rather than a string field.
    if (route) {
      edits.route = route;
      // Action/Decision fan-out materialises from THIS correction carrier; it
      // does not dereference the superseded Daily Log for unchanged text. Keep
      // ordinary edits sparse, but a newly requested fan-out must carry the
      // record's effective human text or it creates an empty destination row.
      if (edits.anchor_text === undefined && edits.transcript === undefined) {
        const existingAnchor = payloadStr(record, "anchor_text");
        const existingTranscript = payloadStr(record, "transcript");
        if (existingAnchor) edits.anchor_text = existingAnchor;
        else if (existingTranscript) edits.transcript = existingTranscript;
      }
    }

    if (record.state === "rejected") {
      // Design §1.2: a REJECTED capture was refused server-side, so there is
      // nothing to supersede and nothing to undo — an edit re-saves as a FRESH
      // capture group (new capture_id + client_id). The rejected row itself is
      // never mutated; it stays in the log as the record of what was refused.
      const capture_id = mintCaptureId();
      await saveToProject({
        capture_id,
        client_id: mintClientId(),
        owner_principal,
        tenant_id: record.envelope.tenant_id,
        skill: record.envelope.skill,
        skill_version: record.envelope.skill_version,
        source_refs: record.envelope.source_refs,
        parts: await getBlobsForCapture(record.capture_id),
        payload: { ...record.envelope.payload, ...edits },
      });
      await releaseForDelivery(capture_id);
    } else if (canUndo(record)) {
      await undoToDraft(record.capture_id);
      await saveToProject({
        capture_id: record.capture_id,
        client_id: record.client_id,
        owner_principal: record.owner_principal ?? owner_principal,
        tenant_id: record.envelope.tenant_id,
        skill: record.envelope.skill,
        skill_version: record.envelope.skill_version,
        source_refs: record.envelope.source_refs,
        // Re-supply the parts so the rebuilt envelope keeps its media; passing
        // [] here would silently strip the photos off the entry being edited.
        parts: await getBlobsForCapture(record.capture_id),
        payload: { ...record.envelope.payload, ...edits },
      });
      await releaseForDelivery(record.capture_id);
    } else {
      const carrier = await submitCorrection(
        record,
        "",
        edits,
        serverOrdinalOf(record.capture_id),
      );
      // The carrier has to be RELEASED or it sits HELD forever and the
      // correction never reaches the coordinator — the old surface minted
      // carriers and never released them, so every post-finalize correction and
      // delete stalled on the device.
      await releaseForDelivery(carrier.capture_id);
    }
    trackRouteMutation(route);
    setCaptureView({ phase: "saved" });
    setDestMeta({});
    setClientGuardFired(false);
    setContextAnswers(null);
    setJudgment({ criticality_tier: null, owner: null, phase: null, trade: null, decides: null, room: null, decides_touched: false, room_touched: false });
    await refreshOutbox();
  }

  /** Delete an existing entry by identity. Delivered records go directly to the
   * authoritative discard command; emitting another CaptureLogged record here
   * was the defect that produced blank replacement logs. */
  async function deleteFromLog(record: OutboxRecord) {
    if (canUndo(record)) {
      await deleteLocalRecord(record.capture_id);
    } else {
      if (project_ref === null || readClient === null) {
        setComingSoon("Couldn't delete this entry while the server is unavailable.");
        return;
      }
      const outcome = await readClient.discardLog(record.capture_id, { project_ref });
      if (!outcome.ok) {
        setComingSoon(`Couldn't delete this entry (${outcome.error}).`);
        return;
      }
      setDiscardedLogIds((current) => new Set(current).add(record.capture_id));
      // The outbox is a delivery/cache, not authority after server discard. Its
      // media blobs remain under the existing retention policy.
      await deleteLocalRecord(record.capture_id);
      await readCacheRemoveItem("logs", project_ref, record.capture_id);
    }
    setCaptureView({ phase: "home" });
    setTab("log");
    await refreshOutbox();
    await logsRead.refresh();
  }

  /** §7.M5: a Master Action List status/criticality change → ONE supersede
   * carrier referencing the canonical item (mirrors status.rs's
   * `build_supersede_carrier`; the coordinator maps the payload to the FSM +
   * canonical judgment — a backend follow-up, like the criticality/owner
   * forwarding). AC-1g-07: project_ref is UNCONDITIONAL — refuse to emit an
   * unscoped Event rather than silently omit it. */
  async function commitMasterStatus(item: MasterItem, change: StatusChange) {
    if (project_ref === null) return;
    const carrierFields = await buildCarrierFields(item.id, 0);
    const payload = markCarrierPayload({
      project_ref,
      ...carrierFields,
      status: change.status,
      // What the screen was showing, so the skill can tell a real status change
      // from a stale form echoing back its own seed. Omitted when unknown rather
      // than guessed — an absent base means "no claim", and the skill skips the
      // check, which is strictly better than asserting a base we invented.
      ...(change.base_status ? { base_status: change.base_status } : {}),
      ...(change.blocker ? { blocker: change.blocker } : {}),
      ...(change.criticality_tier ? { criticality_tier: change.criticality_tier } : {}),
      ...(change.owner ? { owner: change.owner } : {}),
      ...(change.trade ? { trade: change.trade } : {}),
    });
    const capture_id = mintCaptureId();
    await saveToProject({
      capture_id,
      client_id: mintClientId(),
      owner_principal,
      tenant_id: config.tenant_id,
      skill: "master_action_list",
      skill_version: CAPTURE_SKILL_VERSION,
      source_refs: item.source_refs,
      parts: [],
      payload,
    });
    await releaseForDelivery(capture_id);
    void usageClient?.track("action_mutated", project_ref);
    await refreshOutbox();
  }

  function recheckEditedReview(capture_id: string, attempted: string) {
    if (!inferredRoutingEnabled) return;
    const sequence = ++routingReviewSequence.current;
    routingReviewAbort.current?.abort();
    const controller = new AbortController();
    routingReviewAbort.current = controller;
    const scope = { tenant_id: config.tenant_id, project_ref: project_ref ?? "" };
    if (!stt?.transcriber.stageReviewWithDetection) return; // Remain pending: absence of a detector is not a no-detection result.
    void stt.transcriber.stageReviewWithDetection(scope, capture_id, attempted, controller.signal).then((result) => {
      if (!result || sequence !== routingReviewSequence.current) return;
      const detection = result.routing_detection;
      if (!detection || detection.reviewedText !== attempted) return;
      setCaptureView((current) => {
        if (current.phase !== "save" || current.capture_id !== capture_id
            || current.transcript !== attempted || sequence !== routingReviewSequence.current) return current;
        return {
          ...current,
          review_token: result.review_token,
          routing_detection: detection,
          routing_review_pending: false,
        };
      });
    });
  }

  /** §7.M3: long-press alt-ingress. Mint the capture_id + `markOpened` up
   * front (parity with CaptureHome's tap path so the timing pair is emitted),
   * then route to the modality's entry surface. Each entry lands on the shared
   * "save" phase, reaching the SAME SaveScreen/outbox path as a voice capture. */
  function handleSelectModality(modality: ModalityOption) {
    const capture_id = mintCaptureId();
    markOpened(capture_id);
    void usageClient?.track("capture_started", project_ref);
    if (modality === "text") setCaptureView({ phase: "text-entry", capture_id, parts: [] });
    else if (modality === "upload") setCaptureView({ phase: "upload-entry", capture_id });
    else setCaptureView({ phase: "video-entry", capture_id }); // "video"
  }

  /** Text flow: photos accumulate on the same capture screen (peer of voice-
   * record). Appends a primary_photo part, indexed by the current count. */
  async function onTextEntryAddPhoto(capture_id: string, file: File) {
    const sha256 = await sha256HexOfBlob(file);
    const mime = resolveMime(file);
    setCaptureView((prev) => {
      if (prev.phase !== "text-entry") return prev;
      const part: BlobRecord = {
        capture_id,
        part_index: prev.parts.length,
        sha256,
        mime,
        role: roleForModality("photo"),
        size: file.size,
        blob: file,
      };
      return { ...prev, parts: [...prev.parts, part] };
    });
  }

  /** Text Save: the typed note is the capture's transcript-equivalent; it plus
   * any photos land on the shared Save/tag surface. */
  async function onTextEntrySave(capture_id: string, parts: BlobRecord[], note: string) {
    const scope = { tenant_id: config.tenant_id, project_ref: project_ref ?? "" };
    const staged = inferredRoutingEnabled && stt?.transcriber.stageReviewWithDetection
      ? await stt.transcriber.stageReviewWithDetection(scope, capture_id, note)
      : {
          review_token: await stt?.transcriber.stageReview?.(scope, capture_id, note),
          routing_detection: undefined,
        };
    setCaptureView({
      phase: "save",
      capture_id,
      parts,
      transcript: note,
      review_token: staged?.review_token,
      routing_detection: staged?.routing_detection,
    });
  }

  /** Upload-modality file → a primary_photo part (context becomes required at
   * Save, T-10). Video-modality file → a voice_annotation part (its audio is
   * transcribed server-side; the Summary shows no transcript yet). */
  async function onModalityFile(capture_id: string, modality: "photo" | "audio", file: File) {
    const part: BlobRecord = {
      capture_id,
      part_index: 0,
      sha256: await sha256HexOfBlob(file),
      mime: resolveMime(file),
      role: roleForModality(modality),
      size: file.size,
      blob: file,
    };
    setCaptureView({ phase: "save", capture_id, parts: [part] });
  }

  // §7.9: capture the install prompt (never on iOS Safari / unsupported
  // browsers, and never in jsdom — so the banner is inert under test).
  //
  // Task #6: the listener is registered UNCONDITIONALLY now. It used to bail out
  // when the banner had been dismissed before, which also meant the stashed
  // prompt was gone forever — so the InstallGuide, the screen a human opens
  // *deliberately asking to install*, had no prompt to fire and could only
  // narrate manual steps. Dismissing an unsolicited banner is not the same
  // statement as "never let me install"; only the BANNER honors that dismissal
  // (`installBannerDismissed` below), never the guide.
  useEffect(() => {
    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault(); // stash it; we present our own one-time banner
      setInstallPromptEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  }, []);

  const [installBannerDismissed, setInstallBannerDismissed] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem(INSTALL_BANNER_DISMISSED_KEY) === "1",
  );

  function dismissInstallBanner() {
    setInstallBannerDismissed(true);
    if (typeof localStorage !== "undefined") localStorage.setItem(INSTALL_BANNER_DISMISSED_KEY, "1");
  }
  /** Fire the browser's own install prompt. A prompt event is single-use, so it
   * is cleared after firing — which is also what makes the guide fall back to
   * the manual steps on a second visit rather than offering a dead button. */
  async function acceptInstallPrompt() {
    const evt = installPromptEvent;
    dismissInstallBanner();
    setInstallPromptEvent(null);
    if (evt) await evt.prompt();
  }

  function selectTab(id: TabId) {
    setTab(id);
    trackTabView(id);
    setShowInstallGuide(false);
    // An open log entry replaces the whole tab tree below, so without
    // this the tab changed and NOTHING happened on screen — a big,
    // visibly-tappable control that silently did nothing, which the
    // app's own "never a dead tap" principle forbids. Same reason the
    // install guide is dismissed here.
    //
    // The "Saved to project." ack is the same dead tap one screen over
    // (Red, field test 2026-07-29): it is a terminal receipt, and only
    // its own `New capture` button cleared it, so tapping Capture from
    // that screen re-rendered the receipt and looked like nothing had
    // happened. Tapping Capture means "I want to capture something".
    //
    // ONLY the terminal ack. A capture in flight — recording, mid-save,
    // or an entry re-opened for edit — is unfinished work, and dropping
    // it on a stray nav tap would discard something the human typed.
    if (id === "capture" && captureView.phase === "saved") {
      setCaptureView({ phase: "home" });
    }
  }

  return (
    <div data-skin-slot="SK-SHELL" data-testid="app-shell" style={{ position: "relative" }}>
      {/* §7.9: the settings gear owns its own top strip (content never slides
          under it). `order: 3` floats this header to the TOP of the skin's
          column-reverse shell, so the nav stays pinned at the bottom. The top
          padding clears the status bar: with viewport-fit=cover and the
          black-translucent status bar, the installed iOS app draws under it,
          and a gear up there can't be tapped. */}
      <header
        style={{ order: 3, flex: "none", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "max(4px, env(safe-area-inset-top, 0px)) 8px 0" }}
      >
        {/* SW update affordance. Rendered in the gear's existing top strip so it
            costs no new chrome and can't slide under content. Nothing moves on
            its own — the new version applies only on this tap (see
            src/sw/update-channel.ts for why the silent-swap model failed). */}
        {updateReady ? (
          // #UI: styling moved to mark-skin.css so the pill can float
          // bottom-centre above the nav. It used to sit inline beside the
          // settings gear, where a super glancing at the capture button never
          // looked — and a PWA that never updates is how the stale-bundle class
          // of bug survives in the field.
          <button type="button" data-testid="update-pill" onClick={() => void handleUpdateApply()}>
            New version — tap to update
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          data-testid="settings-gear"
          title="Settings"
          aria-label="Settings"
          style={{
            width: "53px",
            height: "53px",
            minHeight: "53px",
            border: "none",
            borderRadius: "50%",
            background: "none",
            color: "var(--mark-accent-amber)",
            fontSize: "29px",
            cursor: "pointer",
          }}
          onClick={() => setSettingsOpen(true)}
        >
          {"⚙︎"}
        </button>
      </header>

      {settingsOpen && (
        <SettingsMenu
          bundleName={bundleName}
          updateStatus={updateStatus}
          updateReady={updateReady}
          onCheckUpdate={() => void handleUpdateCheck()}
          onApplyUpdate={() => void handleUpdateApply()}
          onClose={() => setSettingsOpen(false)}
          onFeedback={() => {
            setTab("feedback");
            setShowInstallGuide(false);
            setSettingsOpen(false);
          }}
          onInstallGuide={() => {
            setShowInstallGuide(true);
            setSettingsOpen(false);
          }}
          onSignOut={
            onSignOut &&
            (() => {
              setSettingsOpen(false);
              onSignOut();
            })
          }
        />
      )}

      <BottomNav
        tab={tab}
        showAdvanced={config.role === "tenant_admin" || ADVANCED_BASIC_ROLES_ENABLED}
        onSelect={selectTab}
      />

      <main data-testid="app-main">
        {/* §7.M7 / §7.10: reusable coming-soon modal for controls whose live
            leg is gated — no dead taps anywhere. */}
        {comingSoon !== null && (
          <ComingSoonModal message={comingSoon} onClose={() => setComingSoon(null)} />
        )}
        {/* Sits above the tab content rather than inside one list, because the
            refusal is per-PROJECT: every surface is empty for the same reason,
            and repeating the line on each one would read as three faults. */}
        {projectRefused && (
          <section
            data-testid="project-refused"
            style={{
              padding: "12px 16px",
              background: "var(--mark-bg-surface)",
              border: "1px solid var(--mark-border-default)",
              borderRadius: "12px",
              color: "var(--mark-text-muted)",
            }}
          >
            {/* Names the person to go to. "Contact your administrator" would be
                jargon on a jobsite (§4 rule 10) and nobody here has that title. */}
            You don’t have access to this project. Ask your project manager to add
            you.
          </section>
        )}
        {/* A refused write used to end here: `markRejected` wrote a reason into
            IndexedDB and nothing rendered it, so the save looked like every other
            save. Above the tabs for the same reason as the line above — the
            refusal is not a property of whichever list happens to be open. */}
        {unseenRejections.map((r) => (
          <section
            key={r.capture_id}
            data-testid={`write-refused-${r.capture_id}`}
            style={{
              padding: "12px 16px",
              background: "var(--mark-bg-surface)",
              border: "1px solid var(--mark-status-critical)",
              borderRadius: "12px",
              color: "var(--mark-text-primary)",
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
            }}
          >
            <span style={{ flex: 1, fontSize: "13px" }}>{rejectionCopy(r.rejection)}</span>
            {/* Dismissing marks it SEEN; it never deletes the record, which stays
                as the account of what was refused. */}
            <button
              type="button"
              data-testid={`write-refused-dismiss-${r.capture_id}`}
              onClick={() => void dismissRejection(r.capture_id)}
              style={{
                background: "none",
                border: "none",
                color: "var(--mark-text-muted)",
                fontSize: "13px",
                minWidth: "44px",
                minHeight: "44px",
              }}
            >
              Got it
            </button>
          </section>
        ))}
        {/* §7.9: one-time home-screen banner (inert where beforeinstallprompt
            is unsupported / under test). Separate from the gear's InstallGuide. */}
        {installPromptEvent && !installBannerDismissed && !showInstallGuide && (
          <InstallBanner
            onInstall={() => void acceptInstallPrompt()}
            onDismiss={dismissInstallBanner}
          />
        )}
        {showInstallGuide && (
          <InstallGuide
            onDismiss={() => setShowInstallGuide(false)}
            // #6: hand the guide the live prompt when the browser offered one,
            // so Android gets one tap instead of a four-step menu recital.
            onInstall={installPromptEvent ? () => void acceptInstallPrompt() : undefined}
          />
        )}
        {!showInstallGuide && (
          <>
        {/* A dedup question is pending work, not Log-tab content. Keep it in
            the active session so the host response is visible without making
            the field user navigate, reload, or restart the installed PWA. */}
        {pendingConfirmRecords.length > 0 && (
          <section data-testid="pending-confirm-surface" aria-label="Duplicate action confirmation">
            {pendingConfirmRecords.map((record) => (
              <PendingConfirmCard
                key={record.capture_id}
                record={record}
                onResolved={() => {
                  // submitConfirmVerdict has already durably released the
                  // verdict before hiding the ASK. Wake (but do not release)
                  // so this exact queued row dispatches in the active session.
                  wakeOutboxNow();
                  void refreshOutbox();
                }}
                onReopen={() => void refreshOutbox()}
              />
            ))}
          </section>
        )}
        {tab === "capture" && (
          <>
            {captureView.phase === "home" && (
              <div style="flex:1;display:flex;flex-direction:column">
                <ProjectSelector
                  project_ids={config.project_ids}
                  selected={project_ref}
                  onSelect={selectProjectForUse}
                />
                <CaptureHome
                  onStartCapture={(capture_id, stream) => {
                    void usageClient?.track("capture_started", project_ref);
                    setCaptureView({ phase: "active", capture_id, parts: [], stream });
                  }}
                  onSelectModality={handleSelectModality}
                />
              </div>
            )}
            {captureView.phase === "text-entry" && (
              <CaptureTextEntry
                partCount={captureView.parts.length}
                onAddPhoto={(file) => void onTextEntryAddPhoto(captureView.capture_id, file)}
                onSave={(note) => onTextEntrySave(captureView.capture_id, captureView.parts, note)}
                onCancel={() => setCaptureView({ phase: "home" })}
              />
            )}
            {captureView.phase === "upload-entry" && (
              <CaptureFileEntry
                accept="image/*"
                title="Upload a photo"
                testidPrefix="upload-capture"
                onFile={(file) => void onModalityFile(captureView.capture_id, "photo", file)}
                onCancel={() => setCaptureView({ phase: "home" })}
              />
            )}
            {captureView.phase === "video-entry" && (
              <CaptureFileEntry
                accept="video/*"
                capture={captureAttr()}
                title="Record a video"
                testidPrefix="video-capture"
                onFile={(file) => void onModalityFile(captureView.capture_id, "audio", file)}
                onCancel={() => setCaptureView({ phase: "home" })}
              />
            )}
            {captureView.phase === "active" && (
              <>
                <ActiveCapture
                  capture_id={captureView.capture_id}
                  initialStream={captureView.stream}
                  onMicrophoneUnavailable={() => setCaptureView({ phase: "home" })}
                  onPartCaptured={(part) =>
                    setCaptureView((prev) =>
                      prev.phase === "active" ? { ...prev, parts: [...prev.parts, part] } : prev,
                    )
                  }
                  onSave={() => {
                    // B3: route through the transcribe leg only when there's a
                    // voice part AND a live STT handle; otherwise (photo-only,
                    // or test/jsdom with no baked endpoint) skip to Save.
                    //
                    // Use a FUNCTIONAL updater (mirroring onPartCaptured above):
                    // ActiveCapture.handleSave appends the audio part via an async
                    // addPart and THEN calls onSave, so reading the render-closure
                    // snapshot of `captureView.parts` here would clobber that
                    // just-committed audio (and any in-flight photo) and force
                    // hasAudio=false (→ transcribe leg never entered). Reading
                    // `prev` carries every committed part and picks the branch
                    // correctly. `stt` is stable render scope — safe to close over.
                    setCaptureView((prev) => {
                      if (prev.phase !== "active") return prev;
                      const hasAudio = prev.parts.some(
                        (p) => p.role === "voice_annotation",
                      );
                      return {
                        phase: hasAudio && stt ? "transcribing" : "save",
                        capture_id: prev.capture_id,
                        parts: prev.parts,
                      };
                    });
                  }}
                />
              </>
            )}
            {captureView.phase === "transcribing" &&
              (() => {
                const parts = captureView.parts;
                const audioPart = parts.find((p) => p.role === "voice_annotation") ?? null;
                const toSave = (
                  transcript: string,
                  low_confidence: boolean,
                  review_token?: string,
                  routing_detection?: RoutingDetection,
                ) => setCaptureView({
                  phase: "save",
                  capture_id: captureView.capture_id,
                  parts,
                  // #32: keep "" (never coalesce to undefined) so SaveScreen
                  // renders the blank EDITABLE transcript field for an
                  // empty-STT voice capture — the human types what they said.
                  transcript,
                  low_confidence,
                  review_token,
                  routing_detection,
                  editing: captureView.editing,
                  from: captureView.from,
                });
                const scope: BlobPutScope = {
                  tenant_id: config.tenant_id,
                  project_ref: project_ref ?? "",
                };
                // stt is non-null here (onSave only routes to `transcribing`
                // when stt exists and an audio part is present).
                return (
                  <TranscribingScreen
                    capture_id={captureView.capture_id}
                    audioPart={audioPart}
                    scope={scope}
                    putBlob={stt!.putBlob}
                    transcriber={stt!.transcriber}
                    inferredRoutingSuggestionsEnabled={inferredRoutingEnabled}
                    onDone={toSave}
                  />
                );
              })()}
            {captureView.phase === "save" && (
              <SaveScreen
                extraction={{}}
                openDecisions={liveDecisions}
                transcript={captureView.transcript}
                lowConfidence={captureView.low_confidence}
                routingDetection={captureView.routing_detection}
                routingReviewPending={captureView.routing_review_pending}
                inferredRoutingSuggestionsEnabled={inferredRoutingEnabled}
                initialRoutingChoice={captureView.routing_choice}
                onRoutingChoiceChange={(choice) =>
                  setCaptureView((prev) => prev.phase === "save"
                    ? { ...prev, routing_choice: choice ?? undefined }
                    : prev)
                }
                onTranscriptChange={(t) => {
                  // A prior detection and choice are bound to the old bytes. Keep
                  // Save closed until the host returns a fresh result for `t`.
                  const mustRecheck = inferredRoutingEnabled && (
                    captureView.routing_detection !== undefined
                    || captureView.routing_review_pending === true
                  );
                  setCaptureView((prev) => prev.phase === "save" ? {
                    ...prev,
                    transcript: t,
                    ...(mustRecheck ? {
                      routing_detection: undefined,
                      routing_choice: undefined,
                      routing_review_pending: true,
                    } : {}),
                  } : prev);
                  if (mustRecheck) recheckEditedReview(captureView.capture_id, t);
                }}
                canRetryTranscription={
                  canRetryTranscription(captureView.transcript, captureView.parts, stt !== null)
                }
                onRetryTranscription={retryCurrentTranscription}
                // The photo-context gate protects a NEW capture before its
                // canonical is committed. A re-opened row emits a text-only
                // correction carrier and must not become uneditable merely
                // because its already-durable canonical has a photo.
                hasPhotoPart={!captureView.editing && hasPhotoPart(captureView.parts)}
                // #39-save: hand SaveScreen the in-flight photo blob(s) themselves,
                // not just the boolean. Pre-commit they exist ONLY here in memory —
                // they don't reach IndexedDB until saveToProject — so the save screen
                // has to render from these to show the photo is being carried over.
                photoBlobs={photoParts(captureView.parts)}
                saving={saving}
                saveError={saveError}
                // A re-opened entry INHERITS its own project (AC-1g-07, the same
                // rule submitCorrection follows) rather than demanding a fresh
                // selection: the entry is already scoped, and making a super
                // re-pick a project to fix a typo would be a gate on an edit that
                // rule #5 does not allow.
                project_ref={
                  captureView.editing
                    ? payloadStr(captureView.editing, "project_ref") ?? project_ref
                    : project_ref
                }
                // §4.3 "every field/flag as it was" — only when re-opened from a
                // saved entry; a fresh capture passes none of these and is
                // unchanged (AC-1g-06: still nothing AI-seeded, these are values
                // this same human typed on this same screen earlier).
                saveBackLabel={
                  captureView.editing
                    ? captureView.from === "report"
                      ? "Report"
                      : "Daily Logs"
                    : undefined
                }
                initialContext={
                  captureView.editing
                    ? {
                        room: payloadStr(captureView.editing, "room") ?? "",
                        // The context block's "what do you want done with it?"
                        // is stored as `anchor_text` (T-10 / §8.1), NOT "intent".
                        intent: payloadStr(captureView.editing, "anchor_text") ?? "",
                      }
                    : undefined
                }
                initialJudgment={
                  captureView.editing
                    ? {
                        criticality_tier: payloadStr(captureView.editing, "criticality_tier") ?? null,
                        owner: payloadStr(captureView.editing, "owner") ?? null,
                        // M-TAG1 (BUI-92): re-open shows the phase/trade this entry
                        // already carries, read back off its own payload (never
                        // AI-seeded — a human set them on this same screen earlier).
                        phase: payloadStr(captureView.editing, "phase") ?? null,
                        trade: payloadStr(captureView.editing, "trade") ?? null,
                        decides: payloadStr(captureView.editing, "decides") ?? null,
                        room: payloadStr(captureView.editing, "room") ?? null,
                      }
                    : undefined
                }
                onBack={() => {
                  // §align: the spec's save-screen-back — cancel the in-progress
                  // capture and return to Record, discarding the draft + the
                  // save-side mirrors (same reset as a post-commit). When the
                  // screen was RE-OPENED from a saved entry there is no draft to
                  // discard, so back simply returns to where it came from.
                  if (captureView.editing) setTab(captureView.from === "report" ? "reports" : "log");
                  setCaptureView({ phase: "home" });
                  setDestMeta({});
                  setClientGuardFired(false);
                  setContextAnswers(null);
                  setJudgment({ criticality_tier: null, owner: null, phase: null, trade: null, decides: null, room: null, decides_touched: false, room_touched: false });
                }}
                // Finding B: each of these four passes its OWN route down BOTH
                // branches now. They used to differ only on the fresh-capture side
                // and converge on one route-less call on the edit side, which is
                // how "edit it into an Action" saved cleanly and reached nothing.
                // The route is the only thing that distinguishes these four
                // callbacks, so dropping it made them all the same button.
                onRouteAction={() =>
                  runSave(() => captureView.editing
                    ? commitEditFromLog(captureView.editing, captureView.transcript, {
                        skill: "master_action_list",
                        op: "upsert",
                      })
                    : commitRoutedCapture(
                        captureView.capture_id,
                        captureView.parts,
                        { skill: "master_action_list", op: "upsert" },
                        captureView.transcript,
                      ))
                }
                onRouteDecisionRaise={() =>
                  runSave(() => captureView.editing
                    ? commitEditFromLog(captureView.editing, captureView.transcript, {
                        skill: "decision_log_dayone",
                        op: "raise",
                      })
                    : commitRoutedCapture(
                        captureView.capture_id,
                        captureView.parts,
                        { skill: "decision_log_dayone", op: "raise" },
                        captureView.transcript,
                      ))
                }
                onRouteDecisionResolve={(decision_ref, rationale) =>
                  runSave(() => captureView.editing
                    ? decisionResolveRoute(decision_ref, rationale).then((route) =>
                        commitEditFromLog(captureView.editing!, captureView.transcript, route),
                      )
                    : decisionResolveRoute(decision_ref, rationale).then((route) =>
                        commitRoutedCapture(
                          captureView.capture_id,
                          captureView.parts,
                          route,
                          captureView.transcript,
                        ),
                      ))
                }
                // Untyped passes NO route on the edit branch, deliberately — not
                // `null` meaning "clear it". Someone who re-opens an entry to fix
                // a typo and saves without touching the toggle must not un-Action
                // it. (The fresh-capture branch DOES pass null: there is nothing
                // there to preserve, and it means "commit with no route".)
                onCommitUntyped={() =>
                  runSave(() => captureView.editing
                    ? commitEditFromLog(captureView.editing, captureView.transcript)
                    : commitRoutedCapture(captureView.capture_id, captureView.parts, null, captureView.transcript))
                }
                // Red's ruling: Delete stays on the log detail. Pre-wire it is a
                // genuine local removal (nothing was ever sent); post-finalize it
                // is a terminal status carrier, never a row deletion (§4.3).
                onDeleteEntry={
                  captureView.editing ? () => void deleteFromLog(captureView.editing!) : undefined
                }
                // Seed the destination boxes from the entry being edited, so the
                // screen shows where this entry ACTUALLY goes. Rendering them
                // blank on an entry that is already on the daily report showed
                // the opposite of the truth and left nothing to un-tick — half
                // of "there is no way to remove it" (Red, 2026-07-31).
                initialDest={
                  captureView.editing
                    ? deserializeDest(
                        captureView.editing.envelope.payload as Record<string, unknown>,
                        { legacyEffective: true },
                      )
                    : undefined
                }
                // Show the Type this entry already IS. A decision raise carries
                // enough route state to restore both Decision and its mode.
                initialToggle={
                  captureView.editing && isAction(captureView.editing)
                    ? "action"
                    : captureView.editing && isDecisionRaise(captureView.editing)
                      ? "decision"
                      : undefined
                }
                initialDecisionMode={
                  captureView.editing && isDecisionRaise(captureView.editing) ? "raise" : undefined
                }
                // `edit` is what makes clearing every box mean "take it off",
                // rather than collapsing to absent = unchanged.
                destMode={captureView.editing ? "edit" : "create"}
                onDestChange={(dest) => setDestMeta(dest)}
                onClientGuardFired={() => setClientGuardFired(true)}
                onComingSoon={setComingSoon}
                onContextChange={(ctx) => setContextAnswers(ctx)}
                onJudgmentChange={(j) => setJudgment(j)}
              />
            )}
            {captureView.phase === "saved" && (
              <div data-testid="capture-saved-ack">
                <p>Saved to project.</p>
                <button type="button" data-testid="capture-new" onClick={() => setCaptureView({ phase: "home" })}>
                  New capture
                </button>
              </div>
            )}
          </>
        )}
        {tab === "log" && (
          <>
            <DailyLog
              records={projectLocalRecords}
              serverOnlyRecords={serverOnlyLogRows}
              projectLabel={project_ref ?? undefined}
              onOpenEntry={(capture_id) => void openLogEntryForEdit(capture_id, "log")}
              onNewCapture={() => {
                // §7.M4: the Daily Logs "+" always starts a fresh capture on
                // the Record tab home (never an inline add on this screen).
                setTab("capture");
                setCaptureView({ phase: "home" });
              }}
            />
          </>
        )}
        {tab === "decisions" && (
          <>
            <DecisionLogView
              decisions={liveDecisions}
              projectLabel={project_ref ?? undefined}
              fetchPhoto={fetchReportPhoto}
              // #16 CLOSED. This tapped through to a "not wired up yet" message
              // because Skill #3 had no op to change a responsible party after a
              // decision was raised — and before THAT it emitted anyway, which was
              // worse than doing nothing: the carrier rode a CaptureLogged whose
              // skill matched the type-route, so it committed as a blank field-log
              // row while the owner never changed. It now mints a real Skill #3
              // `amend` down the same outbox path as a resolve.
              onComingSoon={setComingSoon}
              //
              // Both return the emit promise: it settles once the change is
              // durably QUEUED (ordinal allocated → saved → released), and rejects
              // if it could not be. The tab awaits it to serialize Assign→Resolve
              // (they share one per-decision ordinal cursor) and to show a local
              // failure; whether the server took it comes from `mutations`.
              onAssignOwner={(decision_ref, owner) =>
                decisionAmendRoute(decision_ref, owner).then((route) => emitDecisionRoute(route))
              }
              onResolve={(decision_ref, rationale, reports) =>
                decisionResolveRoute(decision_ref, rationale).then((route) =>
                  emitDecisionRoute(route, reports),
                )
              }
              mutations={decisionMutations}
            />
          </>
        )}
        {tab === "master" && (
          <MasterActionListView
            items={liveActions}
            projectLabel={project_ref ?? undefined}
            fetchPhoto={fetchReportPhoto}
            onShareWithSub={SHARE_WITH_SUB_ENABLED
              ? () => setComingSoon("Sharing with a subcontractor is not available yet.")
              : undefined}
            onCommitStatus={commitMasterStatus}
          />
        )}
        {/* Reports is STILL fixture-backed, deliberately (task #4, 2026-07-27).
            Decisions and the Action List moved to the live read because their
            skills — decision_log_dayone and master_action_list — are REGISTERED
            signed skills, so a read crosses the goalkeeper's verified boundary
            like any other call. `report_digest` is a library module: it is not
            in the signed manifest, so there is no bound skill to read through,
            and adding one is a signing ceremony, not a code change. Wiring
            reports by bypassing the signed boundary would trade the anti-drift
            guarantee for a tab, so it waits for the ceremony instead. */}
        {tab === "reports" && (
          <ReportsView
            // #15: the fixture list is GONE from this tab. It showed old fake
            // reports and no row for today, and once filing became real it would
            // have filed a genuine report for a fixture's window. The two lists
            // below are both real: what can be filed now, and what already is.
            reports={[]}
            fileable={fileableWindows()}
            filed={currentFiledState.items}
            filedStatus={currentFiledState.status}
            onFileWindow={(w) => void fileWindow(w)}
            onRefile={refileFiled}
            onDownload={(f) => void downloadFiled(f)}
            onComingSoon={setComingSoon}
            projectLabel={project_ref ?? undefined}
            projectKey={project_ref ?? undefined}
            key={project_ref ?? "no-project"}
            fetchPdfBytes={fetchPdfBytesStub}
            fetchSidecar={fetchSidecarStub}
            fetchFiledSidecar={fetchFiledSidecar}
            fetchWindowPreview={fetchWindowPreview}
            fetchPhoto={fetchReportPhoto}
            onSetItemIncluded={setReportItemIncluded}
            previewRefreshKey={outboxRecords
              .map((record) => `${record.capture_id}:${record.state}:${record.deliveredAt ?? ""}`)
              .join("|")}
            onOpenItem={(capture_id) => {
              // §7.M7 deep-link into the shared editable save screen (saveFrom:"report").
              //
              // This used to pre-check `outboxRecords` alone and say "needs the
              // live read transport — coming soon" for anything else. That
              // transport has since landed: `openLogEntryForEdit` already falls
              // back to `serverOnlyLogRows`, so the narrower lookup here was
              // refusing to open entries the app was, at that moment, displaying
              // one tab over. Ask the function that knows, and only say something
              // when it genuinely cannot resolve the row.
              void openLogEntryForEdit(capture_id, "report").then((opened) => {
                if (!opened) {
                  setComingSoon("That item's full entry isn't on this device or on the server.");
                }
              });
            }}
            // #5: Mark does not SEND reports — it files the PDF into the folder
            // Mercer mounts, and a human attaches it to an email. The client
            // bucket still takes the §4.7 two-step confirm inside ReportsView
            // (Red is adding a further gate there later), so by the time this
            // fires the human has affirmed it twice.
            onSend={(entry) => void fileReport(entry)}
          />
        )}
        {tab === "feedback" && (
          <FeedbackTab
            tenant_id={config.tenant_id}
            project_ref={project_ref}
            client={feedbackClient}
            records={outboxRecords}
            updateReady={updateReady}
            onSent={(kind) => void usageClient?.track(
              kind === "debug" ? "bug_submitted" : "feedback_submitted",
              project_ref,
            )}
          />
        )}
        {tab === "advanced" && (
          <AdvancedModeGate
            config={config}
            fallback={isSupervisorRole(config)
              ? <section data-testid="supervisor-capability-seam" aria-label="Project oversight">
                  <div style="font-size:20px;font-weight:700;color:var(--mark-text-primary)">Project oversight</div>
                  <p style="margin:0;font-size:12.5px;color:var(--mark-text-muted);line-height:1.5">
                    Your assigned-project PM surface will appear here.
                  </p>
                </section>
              : <p data-testid="advanced-basic-empty">Advanced mode is not available for this role.</p>}
          >
            <UsageAnalyticsView client={usageClient} />
            {/* §7.9: Advanced stays role-gated but ships a "coming in a later
                release" placeholder this round — the live Decision Log /
                Master List power surfaces attach here in a later batch. */}
            <div style="font-size:20px;font-weight:700;color:var(--mark-text-primary)">Advanced</div>
            <p data-testid="advanced-coming-soon" style="margin:0;font-size:12.5px;color:var(--mark-text-muted);line-height:1.5">
              Coming in a later release. This surface will be role-gated
              (tenant_admin only) and mount the full Decision Log and
              Master List power views.
            </p>
          </AdvancedModeGate>
        )}
          </>
        )}
      </main>
    </div>
  );
}
